import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add as AddIcon, Save as SaveIcon } from "@mui/icons-material";

export default function LookupFormDialog({
  open,
  onClose,
  onSubmit,
  item,
  sectionLabel,
  hasColor,
  error,
}) {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [color, setColor] = useState(theme.palette.primary.main);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(item?.name ?? "");
      setColor(item?.color ?? theme.palette.primary.main);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const isEditMode = Boolean(item);
  const canSave = name.trim() !== "";

  const handleSubmit = async () => {
    if (canSave) {
      await onSubmit({ name: name.trim(), ...(hasColor && { color }) });
    }
  };

  const styles = {
    title: { fontWeight: 700, color: "background.text" },
    background: { bgcolor: "background.dialog", color: "background.text" },
    content: { pt: 2, display: "flex", flexDirection: "column", gap: 2 },
    actions: { px: 3, py: 2 },
    button: { textTransform: "none" },
    textField: { mt: 1 },
    swatchButton: {
      position: "relative",
      width: 12,
      height: 12,
      flexShrink: 0,
      borderRadius: "50%",
      overflow: "hidden",
    },
    hiddenColorInput: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      p: 0,
      border: "none",
      opacity: 0,
      cursor: "pointer",
    },
  };

  const swatchSx = { ...styles.swatchButton, bgcolor: color };

  const colorAdornment = hasColor ? (
    <InputAdornment position="start">
      <Box sx={swatchSx}>
        <Box
          component="input"
          type="color"
          aria-label="Pick a color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          sx={styles.hiddenColorInput}
        />
      </Box>
    </InputAdornment>
  ) : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: { sx: styles.background },
        transition: { onEntered: () => nameInputRef.current?.focus() },
      }}
    >
      <DialogTitle sx={styles.title}>
        {isEditMode
          ? `Edit ${sectionLabel} Value`
          : `New ${sectionLabel} Value`}
      </DialogTitle>
      <DialogContent sx={styles.content}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          fullWidth
          required
          label="Name"
          sx={styles.textField}
          inputRef={nameInputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          helperText="Max 100 characters"
          slotProps={{
            htmlInput: { maxLength: 100 },
            input: { startAdornment: colorAdornment },
          }}
        />
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button onClick={onClose} sx={styles.button}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
          disabled={!canSave}
          onClick={handleSubmit}
          sx={styles.button}
        >
          {isEditMode ? "Save Changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
